import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { canShowAppleIAP } from '../../shared/utils/platform';
import { getCachedAuthUser } from '../../services/network';
import { getPrefs, isInQuietHours } from '../../services/notifications';
import { isAnySheetOpen, useAnySheetOpen } from '../../shared/components/BottomSheet';
import { useSubscription } from './useSubscription';
import { useEntitlement } from './useEntitlement';

/**
 * useFirstAsk — mounted once in Layout, right after useSecondAsk
 * (docs/v153-spec.md §2.3).
 *
 * Until 1.5.2 every new account saw the paywall in the same minute it finished
 * set-up, before the app had done anything for the parent. From 1.5.3 set-up
 * ends on /onboarding/done for everyone, and the plans open ONCE, after a day
 * of entries: /paywall?src=day1&reason=day ("Your first day with <baby> is
 * logged"). The server decides when — `firstAsk: { kind: 'day1', since }` on
 * the BABY-SCOPED /subscription/status answer (useEntitlement): the selected
 * baby has at least 3 entries, the earliest logged a day ago, the account is a
 * day old, Free with no Premium from the baby's owner, nothing lapsed, no
 * subscription history, the paywall never seen and Buy never tapped, and day 8
 * not within 48 hours. The rule lives on the server so it can be tuned
 * without an app release.
 *
 * The ask never interrupts. It fires only:
 * - on iOS (the only place Premium can be bought);
 * - once the own status, the babies list and the baby-scoped answer have all
 *   answered (useEntitlement's `settled`, docs/v151-spec.md §6.2) — so a
 *   caregiver covered by the owner's Premium is never asked on a partial
 *   answer;
 * - on Free (the own plan), with no Premium shared by the baby's owner and
 *   nothing lapsed (the lapsed redirect owns that), and while the day-8 second
 *   ask is not due (`secondAsk === null` on the own status: once day 8 has
 *   come, useSecondAsk owns the paywall — never two asks at once);
 * - on the dashboard (`/`) itself — never from a logging page, never
 *   mid-flow. A due answer that lands anywhere else waits for the next
 *   dashboard visit;
 * - outside the user's quiet hours (a 3 a.m. feed is not the moment; the next
 *   daytime visit to the dashboard takes it);
 * - while no bottom sheet is open (v1.5.3 review, §0.4): the dashboard's log,
 *   sleep and Atlas sheets portal over '/', and /paywall sits outside Layout,
 *   so the redirect would unmount a half-filled form. Nothing is written while
 *   a sheet is open, and the ask is judged again when the last one closes;
 * - for a signed-in account that has not seen it on this device.
 * A due answer held back by the dashboard or quiet-hours rule is judged again
 * on the next path change and on every refetch of the baby-scoped answer
 * (`firstAskUpdatedAt`), so the next morning's resume or cold start shows it.
 *
 * ONE marker, not two (useSecondAsk keeps a per-`since` acknowledgement as
 * well): `bt_first_ask_seen:<sub>`, written BEFORE navigating, so closing the
 * app on the paywall never brings the user straight back to it. No storage →
 * no way to remember it → never redirect (better never than on every launch).
 * PaywallPage writes the same marker on EVERY mount, whatever the `src`: the
 * server ends the first ask on any `paywall_viewed`, and this device agrees
 * even while that event is still queued offline (§5.5). There is no
 * notification to withdraw — the 30-minute offer (9960) is only booked when
 * this paywall is closed without a purchase.
 */
export const FIRST_ASK_SEEN_PREFIX = 'bt_first_ask_seen:';
const FIRST_ASK_ROUTE = '/paywall?src=day1&reason=day';

// Fail closed: a marker that cannot be read is treated as seen.
function hasSeenFirstAsk(sub) {
  try {
    return localStorage.getItem(`${FIRST_ASK_SEEN_PREFIX}${sub}`) !== null;
  } catch {
    return true;
  }
}

/**
 * Record that this account has seen the full-screen paywall (now, as an ISO
 * time), so the first ask never opens it by itself on this device. Called by
 * useFirstAsk before it navigates and by PaywallPage on every mount. Returns
 * false when there is no signed-in account or no storage.
 */
export function markFirstAskSeen(sub = getCachedAuthUser()?.sub) {
  if (!sub) return false;
  try {
    localStorage.setItem(`${FIRST_ASK_SEEN_PREFIX}${sub}`, new Date().toISOString());
    return true;
  } catch {
    return false;
  }
}

export function useFirstAsk() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { plan, secondAsk, lapsed, isLoading } = useSubscription();
  const { firstAsk, firstAskUpdatedAt, sharedByOwner, isLoading: entitlementLoading, settled } = useEntitlement();
  // Any BottomSheet open in the app (the dashboard's log, sleep and Atlas sheets).
  const sheetOpen = useAnySheetOpen();

  const kind = firstAsk?.kind;
  const since = typeof firstAsk?.since === 'string' && firstAsk.since ? firstAsk.since : null;
  // Both read strictly: anything but a plain null (an object, or a field this
  // client cannot read) leaves the paywall to the lapsed redirect or the
  // second ask.
  const nothingLapsed = lapsed === null;
  const noSecondAsk = secondAsk === null;

  useEffect(() => {
    if (!canShowAppleIAP()) return;
    if (isLoading || entitlementLoading || !settled) return;
    if (plan !== 'free' || sharedByOwner || !nothingLapsed || !noSecondAsk) return;
    if (kind !== 'day1' || !since) return;
    if (pathname !== '/') return;
    // Never over an open sheet (v1.5.3 review, §0.4): the sheets portal over
    // '/', and /paywall sits outside Layout, so navigating would unmount a
    // half-filled form. Nothing is written; `sheetOpen` is a dependency, so the
    // ask is judged again when the last sheet closes. The live count is read
    // too: a sheet that opened in this same commit has already taken its lock
    // (its effect runs before this one) while `sheetOpen` is still the
    // pre-commit value.
    if (sheetOpen || isAnySheetOpen()) return;
    if (isInQuietHours(new Date(), getPrefs())) return;
    const sub = getCachedAuthUser()?.sub;
    if (!sub || hasSeenFirstAsk(sub)) return;
    // No storage → no way to remember it: better never than on every launch.
    if (!markFirstAskSeen(sub)) return;
    navigate(FIRST_ASK_ROUTE);
  }, [
    plan,
    kind,
    since,
    sharedByOwner,
    nothingLapsed,
    noSecondAsk,
    isLoading,
    entitlementLoading,
    settled,
    pathname,
    sheetOpen,
    navigate,
    // Not read above — a re-run key: the dashboard and quiet-hours rules are
    // judged again on every refetch of the baby-scoped answer, even one that
    // brings the same `since` back (a resume after a real absence, a cold
    // start), so a night-time answer that was due but not shown is shown the
    // next morning without needing a new answer or a path change.
    firstAskUpdatedAt,
  ]);
}

export default useFirstAsk;
